import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import Breadcrumbs from '../../../coreComponents/breadcrumbs';

import { useGetAllCoursesQuery } from '../../../reduxToolkit/reducers/usersSlice';
import { useAppSelector } from '../../../reduxToolkit/hooks';
import { useEffect, useState } from 'react';
import { CourseData, CourseLesson } from '../../../types/Tables';
import LearningListContainer from '../../miscellaneous/learning/learningList';
import { useLocation } from 'react-router-dom';
import { BaseFilters, CourseStateStatus } from '../../../types/Courses';
import { useTranslation } from 'react-i18next';

const StudentCoursesListPageContainer = () => {
  const { t, i18n } = useTranslation();
  const { role, id: studentId, email } = useAppSelector((state) => state.auth);
  const location = useLocation();

  const isMyCourses = location.pathname.includes('my-courses');
  const { data, isSuccess, status } = useGetAllCoursesQuery(isMyCourses ? { role, studentId } : {});
  const [showPrivate, setShowPrivate] = useState(false);
  const [filteredData, setFilteredData] = useState<CourseData[]>([]);

  const [durationFilterSelectors, setDurationFilterSelectors] = useState<{
    duration: {
      filterName: string;
      filterSelectors: { id: number; name: string; text: string; type: string }[];
    };
  }>({
    duration: {
      filterName: t('filters.filterDuration'),
      filterSelectors: [
        {
          id: 0,
          name: 'Less than 20 hours',
          text: t('filters.filterSelector_1', { hours: 20 }),
          type: 'checkbox',
        },
        {
          id: 1,
          name: '20 to 50 hours',
          text: t('filters.filterSelector_2', { fromHours: 20, toHours: 50 }),
          type: 'checkbox',
        },
        {
          id: 2,
          name: 'More than 50 hours',
          text: t('filters.filterSelector_3', { hours: 50 }),
          type: 'checkbox',
        },
      ],
    },
  });

  const [filters, setFilters] = useState<BaseFilters>({
    search: '',
    duration: [],
    status: [],
    courseStateStatus: [],
  });
  useEffect(() => {
    setShowPrivate(isMyCourses);
  }, [isMyCourses]);

  useEffect(() => {
    setDurationFilterSelectors({
      duration: {
        filterName: t('filters.filterDuration'),
        filterSelectors: [
          {
            id: 0,
            name: 'Less than 20 hours',
            text: t('filters.filterSelector_1', { hours: 20 }),
            type: 'checkbox',
          },
          {
            id: 1,
            name: '20 to 50 hours',
            text: t('filters.filterSelector_2', { fromHours: 20, toHours: 50 }),
            type: 'checkbox',
          },
          {
            id: 2,
            name: 'More than 50 hours',
            text: t('filters.filterSelector_3', { hours: 50 }),
            type: 'checkbox',
          },
        ],
      },
    });
  }, [i18n.language]);

  useEffect(() => {
    if (!data) {
      setFilteredData([]);
      return;
    }

    const filtered = data.filter((course: CourseData) => {
      // Filter by search
      const searchLower = filters.search.toLowerCase();

      if (
        filters.search &&
        !(
          course.name.toLowerCase().includes(searchLower) ||
          course.categories.some((cat) => cat?.name?.toLowerCase().includes(searchLower)) ||
          course.categories.some((cat) =>
            cat.lessons.some((lesson: CourseLesson) =>
              lesson?.title?.toLowerCase().includes(searchLower),
            ),
          )
        )
      ) {
        return false;
      }
      if (
        (filters.status.length > 0 &&
          !(
            (course.status === 'in_progress' || course.status === 'completed') &&
            filters.status.includes(course.status) &&
            course.courseStateStatus === CourseStateStatus.ACTIVE
          )) ||
        (filters?.courseStateStatus &&
          filters?.courseStateStatus.length > 0 &&
          !filters?.courseStateStatus.includes(course.courseStateStatus as CourseStateStatus))
      ) {
        return false;
      }

      // Filter by duration range
      if (filters.duration.length > 0) {
        const totalDuration = course.categories
          .flatMap((cat) => cat.lessons)
          .reduce((sum, lesson) => sum + (lesson.video?.length || 0), 0);
        // Match if total duration fits at least one selected duration range
        const matchesDuration = filters.duration.some((range) => {
          if (range === 0) return totalDuration < 20 * 60 * 60;
          if (range === 1) return totalDuration >= 20 * 60 * 60 && totalDuration < 50 * 60 * 60;
          if (range === 2) return totalDuration >= 50 * 60 * 60;
          return false;
        });

        if (!matchesDuration) return false;
      }

      return true;
    });
    setFilteredData(filtered);
  }, [data, filters]);

  return (
    <>
      <Breadcrumbs mainTitle={t('courses.coursesList.coursesListPageTitle')} />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              {/* <CommonCardHeader title={CoursesListCardTitle} tag /> */}

              <CardBody>
                <Row className="mb-3">
                  <Col>
                    {' '}
                    <p>
                      {isMyCourses
                        ? t('students.studentsCoursesListCardParagraphPersonal', { email })
                        : t('students.studentsCoursesListCardParagraphCommon', { email })}
                    </p>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <LearningListContainer
          coursesData={filteredData}
          filters={filters}
          setFilters={setFilters}
          // categoriesFilterSelectors={categoriesFilterSelectors}
          durationFilterSelectors={durationFilterSelectors}
          status={filters.status}
        />
      </Container>
    </>
  );
};
export default StudentCoursesListPageContainer;
